import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import StylishDateTimePicker from 'components/DesignSystems/form/StylishDateTimePicker';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import {
  useUpdateOperationalPeriod,
  useUpdateCurrentOperationalPeriod,
} from 'hooks/incident/useIncidentManagement';
import { usePeriods } from 'components/IAP/hooks/incidentHooks';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import CustomModal from 'components/DesignSystems/New/CustomModal';
import { useCurrentPeriod } from 'components/IAP/hooks/incidentHooks';
import type { OperationalPeriod } from 'components/IAP/hooks/incidentHooks';
import { useIncidentInfo } from 'components/IAP/hooks/incidentInfoHooks';
import {
  usePlannedOperationalPeriods,
  useSaveOperationalAutoAdvance,
  useUpsertPlannedOperationalPeriods,
  type PlannedOperationalPeriod,
  type PlannedWindowInput,
} from 'hooks/incident/usePlannedOperationalPeriods';
import { useHwcgFlag } from 'utils/featureFlags';
import {
  EditableTimeTile,
  PeriodSection,
  ReadOnlyTimeTile,
} from './operationalPeriodModalUi';

/** Matches the server's planned period limits for each scheduling mode. */
const SINGLE_PERIOD_MAX = 1;
const MULTI_PERIOD_MAX = 4;

interface OperationalPeriodModalProps {
  showModal: boolean;
  setShowModal: (show: boolean) => void;
  incident?: { id: string };
}

/**
 * UseCurrentPeriod() return shape varies; use when opRow is missing for
 * read-only labels
 */
type CurrentPeriodForDisplay = {
  startDate?: Dayjs;
  anticipatedEnd?: Dayjs;
};

export type OperationalPeriodCurrentPickersRef = {
  getValues: () => { startIso: string; endIso: string | null };
};

type CurrentPickersProps = {
  opRow: OperationalPeriod;
  disabled: boolean;
};

const OperationalPeriodCurrentPickers = forwardRef<
  OperationalPeriodCurrentPickersRef,
  CurrentPickersProps
>(function OperationalPeriodCurrentPickers({ opRow, disabled }, ref) {
  const [currentStartIso, setCurrentStartIso] = useState(() =>
    dayjs(opRow.start_date_time).toISOString()
  );
  const [currentEndIso, setCurrentEndIso] = useState<string | null>(() =>
    opRow.anticipated_end_date_time
      ? dayjs(opRow.anticipated_end_date_time).toISOString()
      : null
  );

  useImperativeHandle(
    ref,
    () => ({
      getValues: () => ({
        startIso: currentStartIso,
        endIso: currentEndIso,
      }),
    }),
    [currentStartIso, currentEndIso]
  );

  return (
    <>
      <div className="bg-dark bg-opacity-25 mb-3 rounded p-3">
        <div className="text-uppercase txt--sml text-weak mb-3">Started</div>
        <div className="form-label">Date &amp; time</div>
        <StylishDateTimePicker
          value={
            currentStartIso && dayjs(currentStartIso).isValid()
              ? dayjs(currentStartIso).toDate()
              : dayjs(opRow.start_date_time).toDate()
          }
          onChange={(newValue) => {
            const v = newValue as Date | string | null;
            if (v instanceof Date) {
              setCurrentStartIso(v.toISOString());
            } else if (typeof v === 'string') {
              setCurrentStartIso(v);
            }
          }}
          type="datetime-local"
          disabled={disabled}
        />
      </div>
      <div className="bg-dark bg-opacity-25 rounded p-3">
        <div className="text-uppercase txt--sml text-weak mb-3">
          Scheduled end
        </div>
        <div className="form-label">Date &amp; time</div>
        <StylishDateTimePicker
          value={
            currentEndIso
              ? dayjs(currentEndIso).toDate()
              : opRow.anticipated_end_date_time
                ? dayjs(opRow.anticipated_end_date_time).toDate()
                : null
          }
          onChange={(newValue) => {
            const v = newValue as Date | string | null;
            if (v instanceof Date) {
              setCurrentEndIso(v.toISOString());
            } else if (typeof v === 'string') {
              setCurrentEndIso(v);
            }
          }}
          type="datetime-local"
          disabled={disabled}
        />
      </div>
    </>
  );
});

export type NextPeriodPickersRef = {
  getValues: () => { startIso: string | null; endIso: string | null };
};

type NextPickersProps = {
  /**
   * The current period's scheduled end, offered as the start when none is set
   * yet.
   */
  suggestedStartIso: string | null;
  startIso: string | null;
  endIso: string | null;
  disabled: boolean;
};

/**
 * The next period's scheduled window. These are the values every ICS form
 * prints in its operational period box while the working period is being
 * planned, so they are stored on the incident info record rather than on an
 * `operational_period` row — the row for this period does not exist until
 * someone advances.
 */
export const NextOperationalPeriodPickers = forwardRef<
  NextPeriodPickersRef,
  NextPickersProps
>(function NextOperationalPeriodPickers(
  { suggestedStartIso, startIso, endIso, disabled },
  ref
) {
  const [nextStartIso, setNextStartIso] = useState<string | null>(
    () => startIso ?? suggestedStartIso
  );
  const [nextEndIso, setNextEndIso] = useState<string | null>(() => endIso);

  useImperativeHandle(
    ref,
    () => ({
      getValues: () => ({ startIso: nextStartIso, endIso: nextEndIso }),
    }),
    [nextStartIso, nextEndIso]
  );

  return (
    <>
      <div className="mb-3">
        <EditableTimeTile
          label="Scheduled start"
          valueIso={nextStartIso}
          onChangeIso={setNextStartIso}
          disabled={disabled}
          testId="op-period-next-start"
        />
      </div>
      <EditableTimeTile
        label="Scheduled end"
        valueIso={nextEndIso}
        onChangeIso={setNextEndIso}
        disabled={disabled}
        testId="op-period-next-end"
      />
    </>
  );
});

export type PlannedPeriodsEditorRef = {
  getValues: () => PlannedWindowInput[];
};

type PlannedPeriodDraft = {
  key: string;
  id: string | null;
  startIso: string | null;
  endIso: string | null;
};

type PlannedPeriodsEditorProps = {
  currentPeriodNumber: number;
  periods: PlannedOperationalPeriod[];
  /** Used when nothing is planned yet: the window ICS forms print today. */
  fallbackStartIso: string | null;
  fallbackEndIso: string | null;
  maxPeriods: number;
  disabled: boolean;
};

let draftKeySeed = 0;
const nextDraftKey = () => `new-${(draftKeySeed += 1)}`;

/**
 * The scheduled periods after the current one, in order. Advancing makes the
 * first one current, along with its roster.
 */
export const PlannedPeriodsEditor = forwardRef<
  PlannedPeriodsEditorRef,
  PlannedPeriodsEditorProps
>(function PlannedPeriodsEditor(
  {
    currentPeriodNumber,
    periods,
    fallbackStartIso,
    fallbackEndIso,
    maxPeriods,
    disabled,
  },
  ref
) {
  const [drafts, setDrafts] = useState<PlannedPeriodDraft[]>(() =>
    periods.length > 0
      ? periods.map((period) => ({
          key: period.id,
          id: period.id,
          startIso: period.startDateTime,
          endIso: period.anticipatedEndDateTime,
        }))
      : [
          {
            key: nextDraftKey(),
            id: null,
            startIso: fallbackStartIso,
            endIso: fallbackEndIso,
          },
        ]
  );

  useImperativeHandle(
    ref,
    () => ({
      getValues: () =>
        drafts
          .filter((draft) => draft.id || draft.startIso || draft.endIso)
          .map((draft) => ({
            id: draft.id,
            startDateTime: draft.startIso,
            anticipatedEndDateTime: draft.endIso,
          })),
    }),
    [drafts]
  );

  const patch = (key: string, next: Partial<PlannedPeriodDraft>) =>
    setDrafts((prev) =>
      prev.map((draft) => (draft.key === key ? { ...draft, ...next } : draft))
    );

  return (
    <>
      {drafts.map((draft, index) => {
        const periodNumber = currentPeriodNumber + 1 + index;
        return (
          <PeriodSection
            key={draft.key}
            testId={
              index === 0 ? 'op-period-next' : `op-period-planned-${index}`
            }
            title={`Period ${periodNumber}`}
            badge={<span className="badge bg-secondary">Scheduled</span>}
            summary={
              index === 0
                ? 'Every ICS form prints this window in its operational period box while this period is being planned.'
                : undefined
            }
            headerAction={
              drafts.length > 1 || draft.id ? (
                <StylishNewButton
                  type="button"
                  className="button--secondary"
                  disabled={disabled}
                  onClick={() =>
                    setDrafts((prev) =>
                      prev.filter((item) => item.key !== draft.key)
                    )
                  }
                >
                  Remove
                </StylishNewButton>
              ) : null
            }
          >
            <div className="mb-3">
              <EditableTimeTile
                label="Scheduled start"
                valueIso={draft.startIso}
                onChangeIso={(startIso) => patch(draft.key, { startIso })}
                disabled={disabled}
                testId={index === 0 ? 'op-period-next-start' : undefined}
              />
            </div>
            <EditableTimeTile
              label="Scheduled end"
              valueIso={draft.endIso}
              onChangeIso={(endIso) => patch(draft.key, { endIso })}
              disabled={disabled}
              testId={index === 0 ? 'op-period-next-end' : undefined}
            />
          </PeriodSection>
        );
      })}
      {drafts.length < maxPeriods ? (
        <StylishNewButton
          type="button"
          className="button--secondary mb-3"
          disabled={disabled}
          onClick={() =>
            setDrafts((prev) => [
              ...prev,
              {
                key: nextDraftKey(),
                id: null,
                startIso: prev[prev.length - 1]?.endIso ?? null,
                endIso: null,
              },
            ])
          }
        >
          {drafts.length === 0
            ? 'Schedule the next operational period'
            : 'Schedule another operational period'}
        </StylishNewButton>
      ) : null}
    </>
  );
});

export const OperationalPeriodModal: React.FC<OperationalPeriodModalProps> = ({
  showModal,
  setShowModal,
  incident: propIncident,
}) => {
  const [date, setDate] = useState<Date | string | null>(null);
  const [previousExpanded, setPreviousExpanded] = useState(false);
  const currentPickersRef =
    React.useRef<OperationalPeriodCurrentPickersRef>(null);
  const plannedEditorRef = React.useRef<PlannedPeriodsEditorRef>(null);
  const multiPeriodScheduling = useHwcgFlag();
  /** Null until the checkbox is touched, so the saved value shows. */
  const [autoAdvanceDraft, setAutoAdvanceDraft] = useState<boolean | null>(
    null
  );

  const { currentIncident: reduxIncident } = useSelector((state: unknown) => {
    return (state as { app: { currentIncident?: { id: string } } }).app;
  });
  const rbacPermissions = useSelector(
    (state: unknown) =>
      (
        state as {
          app: {
            validateRBACPermissionForActionResult?: Record<string, boolean>;
          };
        }
      ).app.validateRBACPermissionForActionResult
  );
  const canEditIncident = !!rbacPermissions?.['incident:create'];

  const incident = propIncident || reduxIncident;

  const previousMutation = useUpdateOperationalPeriod();
  const currentMutation = useUpdateCurrentOperationalPeriod();
  const { data: incidentInfo } = useIncidentInfo();
  const planned = usePlannedOperationalPeriods(incident?.id);
  const plannedMutation = useUpsertPlannedOperationalPeriods(incident?.id);
  const autoAdvanceMutation = useSaveOperationalAutoAdvance(incident?.id);
  const savedAutoAdvance = planned.view.autoAdvance === true;
  const autoAdvance = autoAdvanceDraft ?? savedAutoAdvance;
  const periods = usePeriods();
  const currPeriod = useCurrentPeriod();
  const prevPeriod = periods.data?.find(
    (p) => p.period_number === currPeriod.periodNumber - 1
  );

  const opRow = currPeriod.currentPeriod;
  const currPeriodForDisplay = currPeriod as CurrentPeriodForDisplay;

  const previousPeriodPickerValue: Date | null = (() => {
    if (date === null || date === undefined) {
      return prevPeriod?.end_date_time
        ? dayjs(prevPeriod.end_date_time).toDate()
        : null;
    }
    return date instanceof Date ? date : new Date(date);
  })();

  const willSavePrevious =
    currPeriod.periodNumber > 0 &&
    !!prevPeriod &&
    previousPeriodPickerValue !== null;

  const willSaveCurrent = !!opRow;

  // The next period's scheduled window is always editable, even before an `operational_period` row exists for the
  // current one, so there is always something Save can write.
  const savePending =
    previousMutation.isPending ||
    currentMutation.isPending ||
    plannedMutation.isPending ||
    autoAdvanceMutation.isPending;

  const showPreviousCard = currPeriod.periodNumber > 0 && !!prevPeriod;

  const toIsoOrNull = (
    value: Date | string | null | undefined
  ): string | null =>
    value && dayjs(value).isValid() ? dayjs(value).toISOString() : null;

  const nextStartIso = toIsoOrNull(
    incidentInfo?.nextOperationalPeriodStartDateTime
  );
  const nextEndIso = toIsoOrNull(
    incidentInfo?.nextOperationalPeriodEndDateTime
  );
  const suggestedNextStartIso = toIsoOrNull(opRow?.anticipated_end_date_time);

  const plannedKey = planned.view.periods
    .map(
      (period) =>
        `${period.id}:${period.startDateTime ?? ''}:${period.anticipatedEndDateTime ?? ''}`
    )
    .join('|');
  const periodSectionsCount =
    (showPreviousCard ? 1 : 0) + 1 + Math.max(1, planned.view.periods.length);
  const activeSectionsCount = opRow ? 1 : 0;

  const handleSaveAll = async () => {
    if (!incident?.id || !canEditIncident) {
      return;
    }
    const incidentId = incident.id;
    try {
      if (willSavePrevious && previousPeriodPickerValue) {
        await previousMutation.mutateAsync({
          incidentId,
          newEndTime: previousPeriodPickerValue.toISOString(),
          operationalPeriod: currPeriod.periodNumber - 1,
          showToast: false,
        });
      }
      if (willSaveCurrent) {
        const snapshot = currentPickersRef.current?.getValues();
        if (!snapshot?.startIso) {
          return;
        }
        await currentMutation.mutateAsync({
          incidentId,
          startDateTime: snapshot.startIso,
          anticipatedEndDateTime: snapshot.endIso,
          showToast: false,
        });
      }
      const plannedSnapshot = plannedEditorRef.current?.getValues();
      if (plannedSnapshot && planned.isSuccess) {
        await plannedMutation.mutateAsync(plannedSnapshot);
      }
      if (multiPeriodScheduling && autoAdvance !== savedAutoAdvance) {
        await autoAdvanceMutation.mutateAsync(autoAdvance);
      }
      setAutoAdvanceDraft(null);
      toast.success('Operational period saved');
      setShowModal(false);
    } catch {
      // mutation hooks already surface errors
    }
  };

  const closeModal = () => {
    setDate(null);
    setAutoAdvanceDraft(null);
    setPreviousExpanded(false);
    setShowModal(false);
  };

  const startedPrev = prevPeriod?.start_date_time
    ? dayjs(prevPeriod.start_date_time).format('MM/DD/YYYY [at] h:mm A')
    : '—';
  const endedPrev = prevPeriod?.end_date_time
    ? dayjs(prevPeriod.end_date_time).format('MM/DD/YYYY [at] h:mm A')
    : '—';

  return (
    <CustomModal
      show={showModal}
      heading="Operational periods"
      onClose={closeModal}
      onConfirm={() => {
        void handleSaveAll();
      }}
      saveButton="Save changes"
      buttonDisable={!canEditIncident || savePending}
    >
      <div>
        {showPreviousCard && (
          <PeriodSection
            title={`Period ${prevPeriod.period_number}`}
            badge={<span className="badge bg-secondary">Completed</span>}
            headerAction={
              !previousExpanded && canEditIncident ? (
                <StylishNewButton
                  type="button"
                  className="button--secondary"
                  disabled={savePending}
                  onClick={() => setPreviousExpanded(true)}
                >
                  Edit
                </StylishNewButton>
              ) : null
            }
          >
            {previousExpanded ? (
              <>
                <div className="text-weak small mb-3">
                  Started (read-only): {startedPrev}
                </div>
                <div className="bg-dark bg-opacity-25 rounded p-3">
                  <div className="text-uppercase txt--sml text-weak mb-3">
                    Ended
                  </div>
                  <div className="form-label">Date &amp; time</div>
                  <StylishDateTimePicker
                    value={previousPeriodPickerValue}
                    onChange={(newDate) => {
                      setDate(newDate ? new Date(newDate) : null);
                    }}
                    type="datetime-local"
                    required={true}
                    disabled={!canEditIncident || savePending}
                  />
                </div>
              </>
            ) : (
              <div className="row g-2">
                <div className="col-md-6">
                  <ReadOnlyTimeTile
                    label="Started"
                    valueFormatted={startedPrev}
                  />
                </div>
                <div className="col-md-6">
                  <ReadOnlyTimeTile label="Ended" valueFormatted={endedPrev} />
                </div>
              </div>
            )}
          </PeriodSection>
        )}

        {opRow ? (
          <PeriodSection
            title={`Period ${currPeriod.periodNumber}${
              currPeriod.periodNumber === 0
                ? ' (ICS-201 development and briefing)'
                : ''
            }`}
            badge={<span className="badge bg-primary">Active</span>}
            highlight
          >
            {showModal ? (
              <OperationalPeriodCurrentPickers
                ref={currentPickersRef}
                key={`${opRow.id}-${opRow.start_date_time}-${opRow.anticipated_end_date_time ?? 'none'}`}
                opRow={opRow}
                disabled={!canEditIncident || savePending}
              />
            ) : null}
          </PeriodSection>
        ) : (
          <PeriodSection
            title="Active period"
            badge={<span className="badge bg-secondary">Unavailable</span>}
          >
            <div className="text-weak small">
              <div className="mb-2">
                Started:{' '}
                {currPeriodForDisplay.startDate?.isValid()
                  ? currPeriodForDisplay.startDate.format('MM/DD/YYYY HH:mm')
                  : '—'}
              </div>
              <div>
                Scheduled end:{' '}
                {currPeriodForDisplay.anticipatedEnd?.isValid()
                  ? currPeriodForDisplay.anticipatedEnd.format(
                      'MM/DD/YYYY HH:mm'
                    )
                  : '—'}
              </div>
            </div>
          </PeriodSection>
        )}

        {showModal && planned.isSuccess ? (
          <PlannedPeriodsEditor
            ref={plannedEditorRef}
            key={plannedKey}
            currentPeriodNumber={currPeriod.periodNumber}
            periods={planned.view.periods}
            fallbackStartIso={nextStartIso ?? suggestedNextStartIso}
            fallbackEndIso={nextEndIso}
            maxPeriods={
              multiPeriodScheduling ? MULTI_PERIOD_MAX : SINGLE_PERIOD_MAX
            }
            disabled={!canEditIncident || savePending}
          />
        ) : null}

        {multiPeriodScheduling && planned.isSuccess ? (
          <div className="form-check mt-3">
            <input
              id="op-auto-advance"
              type="checkbox"
              className="form-check-input"
              checked={autoAdvance}
              disabled={!canEditIncident || savePending}
              onChange={(event) => setAutoAdvanceDraft(event.target.checked)}
            />
            <label className="form-check-label" htmlFor="op-auto-advance">
              Automatically advance at each scheduled start
            </label>
            <div className="text-weak txt--sml">
              Advances to the next planned period when its start time arrives
              and stops after the last one. Planning calendar events and the IAP
              export still need the manual Advance action.
            </div>
          </div>
        ) : null}

        <div className="text-weak txt--sml mt-2 mb-0">
          {periodSectionsCount} period section
          {periodSectionsCount === 1 ? '' : 's'} shown
          {activeSectionsCount > 0 ? ` · ${activeSectionsCount} active` : ''}
        </div>
      </div>
    </CustomModal>
  );
};
